import { z } from 'zod';
import { apiClient, apiGet, apiPost } from './client';
import { parseOrWarn } from '@/lib/zod-resolver';
import { paginationSchema } from './_types/common';
import type { Pagination } from './_types/common';

/**
 * 监控/对比 API（v4 M3-ρ-5）— 接 M3-η 后端 7 endpoint。
 *
 * 后端契约源：backend_app/app/api/v1/monitoring.py + backend_app/app/schemas/monitoring.py
 *   GET  /monitoring/compliance      ?subject_id&policy_pack_id&at_time?
 *   GET  /monitoring/cross-pack      ?subject_id&policy_pack_ids[](≥2,≤10)
 *   GET  /monitoring/cross-subject   ?policy_pack_id&subject_ids?[](≤200)
 *   GET  /monitoring/trend           ?subject_id&policy_pack_id&since?&until?&granularity=daily
 *   POST /monitoring/snapshots                       body 四模式互斥
 *   GET  /monitoring/snapshots                       ?subject_id?&policy_pack_id?&snapshot_type?&page&page_size
 *   GET  /monitoring/snapshots/{id}
 *
 * 权限点：
 *   - monitoring:read     — STAFF/TENANT_ADMIN/PLATFORM
 *   - monitoring:snapshot — 同上（POST 物化触发）
 *
 * v1 阶段后端 trend granularity 仅支持 daily（weekly/monthly → 400）。
 */

// === 公共原子 schema（与 schemas/monitoring.py 对位） ===

// IndicatorEvalOut — children 递归
// Sprint 50:加 policy_ref(政策原文引用)+ auto_compute(自动/人工)
export interface IndicatorEvalNode {
  code: string;
  name: string;
  actual: number | string | null;
  required: number | string | null;
  passed: boolean | null;
  severity: 'ok' | 'fail' | 'pending';
  note: string;
  /** 政策原文引用 / 口径说明(Sprint 50);老 BE 缺该字段时 undefined */
  policy_ref?: string;
  /** True=系统自动算 / False=人工填(Sprint 50);老 BE 缺该字段时 undefined(等同 true) */
  auto_compute?: boolean;
  children: IndicatorEvalNode[];
}

export const indicatorEvalSchema: z.ZodType<IndicatorEvalNode> = z.lazy(() =>
  z.object({
    code: z.string(),
    name: z.string(),
    actual: z.union([z.number(), z.string()]).nullable(),
    required: z.union([z.number(), z.string()]).nullable(),
    passed: z.boolean().nullable(),
    severity: z.enum(['ok', 'fail', 'pending']),
    note: z.string(),
    // Sprint 50 — 老 snapshot / period 路径可能缺该字段;保持向后兼容
    policy_ref: z.string().optional(),
    auto_compute: z.boolean().optional(),
    children: z.array(indicatorEvalSchema),
  }),
);

export const packComplianceSummarySchema = z.object({
  policy_pack_id: z.string().uuid(),
  policy_pack_code: z.string().nullable(),
  policy_pack_version: z.string().nullable(),
  target_level: z.string().nullable(),
  quantitative_total: z.number().int().nonnegative(),
  passed_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  pending_count: z.number().int().nonnegative(),
  ready_data: z.boolean(),
  conclusion: z.string(),
});
export type PackComplianceSummary = z.infer<typeof packComplianceSummarySchema>;

export const subjectComplianceRowSchema = z.object({
  subject_id: z.string().uuid(),
  subject_display_name: z.string().nullable(),
  passed_count: z.number().int().nonnegative(),
  quantitative_total: z.number().int().nonnegative(),
  pass_rate: z.number().min(0).max(1),
  ready_data: z.boolean(),
  rank: z.number().int().positive(),
});
export type SubjectComplianceRow = z.infer<typeof subjectComplianceRowSchema>;

// === Payload 三种 discriminated 形态 ===

export const complianceSnapshotPayloadSchema = z.object({
  snapshot_type: z.literal('compliance_state'),
  summary: packComplianceSummarySchema,
  indicators: z.array(indicatorEvalSchema),
});
export type ComplianceSnapshotPayload = z.infer<
  typeof complianceSnapshotPayloadSchema
>;

export const crossPackPayloadSchema = z.object({
  snapshot_type: z.literal('cross_pack'),
  subject_id: z.string().uuid(),
  subject_display_name: z.string().nullable(),
  packs: z.array(packComplianceSummarySchema),
});
export type CrossPackPayload = z.infer<typeof crossPackPayloadSchema>;

export const crossSubjectPayloadSchema = z.object({
  snapshot_type: z.literal('cross_subject'),
  policy_pack_id: z.string().uuid(),
  policy_pack_code: z.string().nullable(),
  policy_pack_version: z.string().nullable(),
  rows: z.array(subjectComplianceRowSchema),
});
export type CrossSubjectPayload = z.infer<typeof crossSubjectPayloadSchema>;

// === 即时计算（不落库）出参 ===

export const complianceSnapshotOutSchema = z.object({
  subject_id: z.string().uuid(),
  policy_pack_id: z.string().uuid(),
  computed_at: z.string(),
  source: z.enum(['live', 'snapshot']),
  payload: complianceSnapshotPayloadSchema,
});
export type ComplianceSnapshotOut = z.infer<typeof complianceSnapshotOutSchema>;

export const crossPackComparisonOutSchema = z.object({
  subject_id: z.string().uuid(),
  computed_at: z.string(),
  source: z.enum(['live', 'snapshot']),
  payload: crossPackPayloadSchema,
});
export type CrossPackComparisonOut = z.infer<
  typeof crossPackComparisonOutSchema
>;

export const crossSubjectComparisonOutSchema = z.object({
  policy_pack_id: z.string().uuid(),
  computed_at: z.string(),
  source: z.enum(['live', 'snapshot']),
  payload: crossSubjectPayloadSchema,
});
export type CrossSubjectComparisonOut = z.infer<
  typeof crossSubjectComparisonOutSchema
>;

// === Trend ===

export const trendPointSchema = z.object({
  snapshot_at: z.string(),
  passed_count: z.number().int().nonnegative(),
  quantitative_total: z.number().int().nonnegative(),
  pass_rate: z.number().min(0).max(1),
  ready_data: z.boolean(),
  source_snapshot_id: z.string().uuid(),
});
export type TrendPoint = z.infer<typeof trendPointSchema>;

export const trendSeriesOutSchema = z.object({
  subject_id: z.string().uuid(),
  policy_pack_id: z.string().uuid(),
  granularity: z.enum(['daily', 'weekly', 'monthly']),
  since: z.string().nullable(),
  until: z.string().nullable(),
  points: z.array(trendPointSchema),
});
export type TrendSeriesOut = z.infer<typeof trendSeriesOutSchema>;

// === 物化快照（落库行） ===

// payload 联合：discriminated by snapshot_type
export const monitoringPayloadSchema = z.discriminatedUnion('snapshot_type', [
  complianceSnapshotPayloadSchema,
  crossPackPayloadSchema,
  crossSubjectPayloadSchema,
]);
export type MonitoringPayload = z.infer<typeof monitoringPayloadSchema>;

export const monitoringSnapshotOutSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  subject_id: z.string().uuid().nullable(),
  policy_pack_id: z.string().uuid().nullable(),
  snapshot_at: z.string().nullable(),
  snapshot_type: z.enum(['compliance_state', 'cross_pack', 'cross_subject']),
  payload: monitoringPayloadSchema,
  source_case_ids: z.array(z.string().uuid()),
  created_by_account_id: z.string().uuid().nullable(),
  created_at: z.string().nullable(),
});
export type MonitoringSnapshotOut = z.infer<typeof monitoringSnapshotOutSchema>;

export const monitoringSnapshotListSchema = paginationSchema(
  monitoringSnapshotOutSchema,
);

// === 触发请求 body ===

/**
 * SnapshotTriggerRequest — 四模式互斥（后端 model_validator 守）：
 *   1. case_id                          ⇒ compliance_state（取 case.subject/pack）
 *   2. subject_id + policy_pack_id      ⇒ compliance_state
 *   3. subject_id + policy_pack_ids(≥2,≤10) ⇒ cross_pack
 *   4. policy_pack_id only              ⇒ cross_subject
 *
 * 注意：不传 snapshot_type；后端按形状推断（防客户端越权类型混淆）。
 */
export interface TriggerSnapshotBody {
  case_id?: string;
  subject_id?: string;
  policy_pack_id?: string;
  policy_pack_ids?: string[];
  note?: string;
}

// === 列表查询参数 ===

export interface ListSnapshotsParams {
  subject_id?: string;
  policy_pack_id?: string;
  snapshot_type?: 'compliance_state' | 'cross_pack' | 'cross_subject';
  page?: number;
  page_size?: number;
}

// === Trend 查询参数 ===

export interface GetTrendParams {
  subject_id: string;
  policy_pack_id: string;
  since?: string;
  until?: string;
  /** v1：仅支持 daily；后端 weekly/monthly → 400 */
  granularity?: 'daily';
}

// === 工具 ===

/** path-param 转义（ρ-1 SR-med#2 教训） */
function _safe(seg: string): string {
  return encodeURIComponent(seg);
}

/**
 * 重复参数序列化（subject_ids / policy_pack_ids）。
 *
 * axios 1.x 默认数组用 `key[]=v1&key[]=v2`，但 FastAPI Query(list[str]) 期望
 * 裸重复 `key=v1&key=v2`。因此构造 URLSearchParams 显式 append，绕过 axios
 * paramsSerializer 默认行为。
 */
function buildSearchParams(
  scalar: Record<string, string | number | undefined>,
  repeating: Record<string, string[] | undefined>,
): URLSearchParams {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(scalar)) {
    if (v === undefined || v === null || v === '') continue;
    usp.append(k, String(v));
  }
  for (const [k, arr] of Object.entries(repeating)) {
    if (!arr) continue;
    for (const v of arr) {
      if (v === undefined || v === null || v === '') continue;
      usp.append(k, v);
    }
  }
  return usp;
}

// === API calls ===

/** GET /monitoring/compliance — 即时单 subject × 单 pack
 *
 * Sprint27-hotfix7:case_id 透传 — BE 用此叠加案级 binding manual_value
 * 不传则走政策包预览口径(无 binding 覆盖)
 */
export async function getCompliance(args: {
  subject_id: string;
  policy_pack_id: string;
  at_time?: string;
  case_id?: string;
}): Promise<ComplianceSnapshotOut> {
  const q: Record<string, string | undefined> = {
    subject_id: args.subject_id,
    policy_pack_id: args.policy_pack_id,
  };
  if (args.at_time) q.at_time = args.at_time;
  if (args.case_id) q.case_id = args.case_id;
  const raw = await apiGet('/monitoring/compliance', q);
  return parseOrWarn(
    complianceSnapshotOutSchema,
    raw,
    'GET /monitoring/compliance',
  );
}

/** GET /monitoring/cross-pack — 单 subject 跨 pack（≥2 ≤10） */
export async function getCrossPack(args: {
  subject_id: string;
  policy_pack_ids: string[];
}): Promise<CrossPackComparisonOut> {
  const usp = buildSearchParams(
    { subject_id: args.subject_id },
    { policy_pack_ids: args.policy_pack_ids },
  );
  // 显式传 URLSearchParams 走 axios 内置序列化（key=v1&key=v2）
  const res = await apiClient.get('/monitoring/cross-pack', { params: usp });
  return parseOrWarn(
    crossPackComparisonOutSchema,
    res.data,
    'GET /monitoring/cross-pack',
  );
}

/** GET /monitoring/cross-subject — 单 pack 跨主体排行 */
export async function getCrossSubject(args: {
  policy_pack_id: string;
  subject_ids?: string[];
}): Promise<CrossSubjectComparisonOut> {
  const usp = buildSearchParams(
    { policy_pack_id: args.policy_pack_id },
    { subject_ids: args.subject_ids },
  );
  const res = await apiClient.get('/monitoring/cross-subject', { params: usp });
  return parseOrWarn(
    crossSubjectComparisonOutSchema,
    res.data,
    'GET /monitoring/cross-subject',
  );
}

/** GET /monitoring/trend — 时序（v1 仅 daily） */
export async function getTrend(
  params: GetTrendParams,
): Promise<TrendSeriesOut> {
  const q: Record<string, string | undefined> = {
    subject_id: params.subject_id,
    policy_pack_id: params.policy_pack_id,
    granularity: params.granularity ?? 'daily',
  };
  if (params.since) q.since = params.since;
  if (params.until) q.until = params.until;
  const raw = await apiGet('/monitoring/trend', q);
  return parseOrWarn(trendSeriesOutSchema, raw, 'GET /monitoring/trend');
}

/** POST /monitoring/snapshots — 触发物化（四模式由 body 形状推断） */
export async function postSnapshot(
  body: TriggerSnapshotBody,
): Promise<MonitoringSnapshotOut> {
  // 不传 snapshot_type — 后端按入参形状推断（防客户端类型混淆越权）
  const payload: Record<string, unknown> = {};
  if (body.case_id) payload.case_id = body.case_id;
  if (body.subject_id) payload.subject_id = body.subject_id;
  if (body.policy_pack_id) payload.policy_pack_id = body.policy_pack_id;
  if (body.policy_pack_ids) payload.policy_pack_ids = body.policy_pack_ids;
  if (body.note) payload.note = body.note;
  const raw = await apiPost('/monitoring/snapshots', payload);
  return parseOrWarn(
    monitoringSnapshotOutSchema,
    raw,
    'POST /monitoring/snapshots',
  );
}

/** GET /monitoring/snapshots — 物化列表 */
export async function listSnapshots(
  params: ListSnapshotsParams = {},
): Promise<Pagination<MonitoringSnapshotOut>> {
  const q: Record<string, unknown> = {
    page: params.page ?? 1,
    page_size: params.page_size ?? 20,
  };
  if (params.subject_id) q.subject_id = params.subject_id;
  if (params.policy_pack_id) q.policy_pack_id = params.policy_pack_id;
  if (params.snapshot_type) q.snapshot_type = params.snapshot_type;
  const raw = await apiGet('/monitoring/snapshots', q);
  return parseOrWarn(
    monitoringSnapshotListSchema,
    raw,
    'GET /monitoring/snapshots',
  );
}

/** GET /monitoring/snapshots/{id} — 单条详情（trend 跳转目标） */
export async function getSnapshot(
  snapshotId: string,
): Promise<MonitoringSnapshotOut> {
  const raw = await apiGet(`/monitoring/snapshots/${_safe(snapshotId)}`);
  return parseOrWarn(
    monitoringSnapshotOutSchema,
    raw,
    `GET /monitoring/snapshots/${snapshotId}`,
  );
}

// === UI 辅助 ===

export const SNAPSHOT_TYPE_LABEL: Record<
  MonitoringSnapshotOut['snapshot_type'],
  string
> = {
  compliance_state: '单主体单 pack',
  cross_pack: '跨 pack 对比',
  cross_subject: '跨主体排行',
};

/** 跨主体排行后端硬上限（与 backend monitoring service 对齐） */
export const CROSS_SUBJECT_HARD_LIMIT = 200;

/** UUID 校验（ρ-3 SR-med#2：入参先校验，避免 400 round-trip） */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isValidUuid(s: string): boolean {
  return UUID_RE.test(s);
}
